import { create } from 'zustand';

/**
 * Receives incoming-call events from the unified realtime WebSocket and
 * exposes the current "ringing" state to the UI. Single-slot for now:
 * portal helpline is 1:1 (a client picks ONE practitioner and the backend
 * rejects overlaps), so a second event for the same psy in the same minute
 * is a duplicate and collapses onto the same slot rather than queueing.
 *
 * The dialog reads `incoming` and renders a ringing modal whenever it's set.
 * `clearIncoming` is called when the psy taps Join (after the navigation has
 * been initiated) or Dismiss.
 */
export interface IncomingPortalCall {
  sessionId: string;
  clientId: string;
  clientName: string;
  /** Location the client is calling FROM. Practitioners cover multiple
   *  locations for portal helpline; surfacing this in the dialog lets the
   *  psy load the right context before picking up. */
  locationName: string;
  concerns: string[];
  startedAt: string;
  /** Local timestamp when the FE received the event — used for "ringing for 12s" display. */
  receivedAt: number;
}

interface IncomingCallState {
  incoming: IncomingPortalCall | null;
  /** True once the SSE stream has handshaken (`open` event received). UI can show a connection chip. */
  streamOpen: boolean;
}

interface IncomingCallActions {
  setIncoming: (call: IncomingPortalCall) => void;
  clearIncoming: () => void;
  setStreamOpen: (open: boolean) => void;
  reset: () => void;
}

type IncomingCallStore = IncomingCallState & IncomingCallActions;

const initialState: IncomingCallState = {
  incoming: null,
  streamOpen: false,
};

export const useIncomingCallStore = create<IncomingCallStore>((set) => ({
  ...initialState,

  setIncoming: (call) => {
    set({ incoming: call });
  },

  clearIncoming: () => {
    set({ incoming: null });
  },

  setStreamOpen: (open) => {
    set({ streamOpen: open });
  },

  reset: () => {
    set(initialState);
  },
}));
